import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import LedgerModal from "./LedgerModal";
import "./ledger-page.css";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrency } from "../../utils/helper";
import { ROLES_TYPE } from "../../utils/enum";

const formatBalance = (balance) => {
  const n = Number(balance) || 0;
  const isNegative = n < 0;
  const isZero = n === 0;

  const formatted = Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return {
    display: `${isNegative ? "-" : ""}₹${formatted}`,
    className: isZero || !isNegative ? "text-success-600" : "text-danger-600",
  };
};

const getRoleBadgeClass = (role) => {
  switch (role?.toLowerCase()) {
    case "admin":
      return "bg-danger-focus text-danger-600";
    case "sub_admin":
      return "bg-warning-focus text-warning-600";
    case "broker":
      return "bg-primary-100 text-primary-600";
    case "user":
    default:
      return "bg-success-focus text-success-600";
  }
};


const normalizeLedgerResponse = (result) => {
  if (!result || result.errors) {
    return {
      list: [],
      total: 0,
      totalPages: 0,
      currentPage: 1,
      hasNextPage: false,
      hasPrevPage: false,
    };
  }
  const meta = result.meta || {};
  const total = result.total ?? meta.total ?? 0;
  const totalPages =
    total === 0 ? 0 : Math.max(1, result.totalPages ?? meta.totalPages ?? 1);
  const currentPage = result.currentPage ?? meta.page ?? 1;
  const hasNextPage =
    result.hasNextPage ?? (total > 0 && currentPage < totalPages);
  const hasPrevPage = result.hasPrevPage ?? currentPage > 1;
  return {
    list: Array.isArray(result.data) ? result.data : [],
    total,
    totalPages,
    currentPage,
    hasNextPage,
    hasPrevPage,
  };
};

const LedgerLayer = () => {
  const { AsyncGetApiCall } = useApi();
  const [accountType, setAccountType] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [limitVal, setLimitVal] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [showZeroOnly, setShowZeroOnly] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPagination, setIsPagination] = useState(true);

  const fetchLedger = useCallback(
    async (role, page, limit) => {
      setLoading(true);
      setError(null);
      const params = {
        pageNo: page,
        limitVal: limit,
        isPagination: isPagination
      };
      if (role) {
        params.roleType = role;
      }

      const result = await AsyncGetApiCall("/accounts/my-accounts", params);
      if (result?.errors) {
        setError(result.errors[0] || "Failed to fetch ledger data");
        setLedgerRows([]);
        setTotal(0);
        setTotalPages(0);
        setCurrentPage(1);
        setHasNextPage(false);
        setHasPrevPage(false);
      } else if (result?.success) {
        const norm = normalizeLedgerResponse(result);
        const startIndex = (norm.currentPage - 1) * limit;
        const mapped = norm.list.map((account, index) => {
          const { display, className } = formatBalance(account.currentBalance);
          return {
            sr: startIndex + index + 1,
            acNo: account.loginId,
            role: account.role,
            name: account.name,
            isMaster: (account.name || "").toLowerCase().includes("master"),
            balance: display,
            balanceClass: className,
            rawBalance: Number(account.currentBalance),
            _id: account._id,
          };
        });
        setLedgerRows(mapped);
        setTotal(norm.total);
        setTotalPages(norm.totalPages);
        setCurrentPage(norm.currentPage);
        setHasNextPage(norm.hasNextPage);
        setHasPrevPage(norm.hasPrevPage);
      } else {
        setError(result?.message || "Failed to fetch ledger data");
        setLedgerRows([]);
        setTotal(0);
        setTotalPages(0);
        setCurrentPage(1);
        setHasNextPage(false);
        setHasPrevPage(false);
      }
      setLoading(false);
    },
    [AsyncGetApiCall],
  );

  useEffect(() => {
    fetchLedger(undefined, 1, limitVal);
    // Initial ledger load only (limit / filters use dedicated handlers)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLedger]);

  useEffect(() => {
    fetchLedger(accountType || undefined, 1, limitVal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPagination]);

  const handleApply = () => {
    fetchLedger(accountType || undefined, 1, limitVal);
  };

  const handleClear = () => {
    setAccountType("");
    fetchLedger(undefined, 1, limitVal);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchLedger(accountType || undefined, newPage, limitVal);
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setLimitVal(newLimit);
    fetchLedger(accountType || undefined, 1, newLimit);
  };

  const handleShowZeroBalance = () => {
    setShowZeroOnly((z) => !z);
  };

  const filteredData = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return ledgerRows;
    return ledgerRows.filter(
      (row) =>
        row.name.toLowerCase().includes(q) ||
        String(row.acNo).includes(searchTerm.trim()),
    );
  }, [ledgerRows, searchTerm]);

  const displayRows = useMemo(() => {
    if (!showZeroOnly) return filteredData;
    return filteredData.map((row) => ({ ...row, balance: "₹0.00" }));
  }, [filteredData, showZeroOnly]);

  console.log("displayRows", displayRows)

  const getAllAccounts = async () => {
    const params = {
      pageNo: 1,
      limitVal: 100000,
      isPagination: false,
    };

    if (accountType) {
      params.roleType = accountType;
    }

    const result = await AsyncGetApiCall("/accounts/my-accounts", params);

    if (result?.errors || !result?.success) {
      throw new Error(result?.errors?.[0] || "Failed to fetch export data");
    }

    const norm = normalizeLedgerResponse(result);

    return norm.list.map((account, index) => ({
      sr: index + 1,
      acNo: account.loginId,
      role: account.role,
      name: account.name,
      balance: formatCurrency(account.currentBalance),
      rawBalance: Number(account.currentBalance),
    }));
  };

  const handleExportCsv = async () => {
    try {
      const exportRows = await getAllAccounts();

      if (!exportRows?.length) {
        return toast.error("No data available");
      }

      const headers = ["acNo", "name", "role", "balance"];

      const csvRows = [
        headers.join(","),

        ...exportRows.map((item) =>
          headers.map((header) => `"${item?.[header] ?? ""}"`).join(",")
        ),
      ];

      const csvString = csvRows.join("\n");

      const blob = new Blob([csvString], {
        type: "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "users-data.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("CSV downloaded successfully");
    } catch (error) {
      console.log(error);
      toast.error(error.message || "Failed to export CSV");
    }
  };

  const handleExportPdf = async () => {
    try {
      const exportRows = await getAllAccounts();

      if (!exportRows?.length) {
        return toast.error("No data available");
      }

      const formattedBalance = (rawBalance) =>
        Number(rawBalance || 0)
          .toString()
          .match(/^-?\d+(?:\.\d{0,2})?/)[0];

      const doc = new jsPDF();

      const tableColumn = ["Login ID", "Name", "Role", "Balance"];

      const tableRows = exportRows.map((item) => [
        item?.acNo,
        item?.name,
        item?.role,
        formattedBalance(item?.rawBalance),
      ]);

      doc.text("Users Report", 14, 15);

      autoTable(doc, {
        startY: 20,
        head: [tableColumn],
        body: tableRows,
      });

      doc.save("users-report.pdf");

      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.log(error);
      toast.error(error.message || "Failed to export PDF");
    }
  };

  const handleLedgerClick = (row) => {
    setSelectedAccount({
      name: row.name,
      id: row.acNo,
      dbId: row._id,
    });
    setIsModalOpen(true);
  };

  const pageButtons = useMemo(() => {
    const n = totalPages;
    if (n <= 0) return [];
    const cap = Math.min(n, 5);
    const out = [];
    for (let i = 0; i < cap; i += 1) {
      let pageNum;
      if (n <= 5) pageNum = i + 1;
      else if (currentPage <= 3) pageNum = i + 1;
      else if (currentPage >= n - 2) pageNum = n - 4 + i;
      else pageNum = currentPage - 2 + i;
      out.push(pageNum);
    }
    return out;
  }, [totalPages, currentPage]);

  return (
    <>
      {(loading) && <Loader />}

      <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-24">
        <div className="d-flex align-items-center gap-2 text-secondary-light">
          <Icon icon="tabler:layout-grid" className="icon text-xl" />
          <Icon icon="tabler:chevron-right" className="icon text-sm" />
          <span className="fw-semibold text-primary-light">Ledger</span>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2 ledger-page-actions">
          <button
            type="button"
            className="btn btn-sm radius-8 px-16 py-8 btn-ledger-csv"
            onClick={handleExportCsv}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="btn btn-sm radius-8 px-16 py-8 btn-ledger-pdf"
            onClick={handleExportPdf}
          >
            Export PDF
          </button>
          <button
            type="button"
            className={`btn btn-sm radius-8 px-16 py-8 btn-ledger-primary ${showZeroOnly ? "opacity-75" : ""
              }`}
            onClick={handleShowZeroBalance}
          >
            {showZeroOnly ? "Show All Balances" : "Show Zero Balance"}
          </button>
        </div>
      </div>

      <div className="card border shadow-sm radius-16 mb-24 ledger-filter-card">
        <div className="card-body p-24">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div style={{ minWidth: "180px" }}>
              <SearchableSelect
                placeholder="Select Type"
                value={accountType}
                showSearch={true}
                onChange={(e) => setAccountType(e.target.value)}
                options={ROLES_TYPE.map((u) => ({
                  label: u.label,
                  value: u.value,
                }))}
              />
            </div>
            <button
              type="button"
              className="btn btn-sm radius-8 px-20 py-10 btn-ledger-apply"
              onClick={handleApply}
            >
              Apply
            </button>
            <button
              type="button"
              className="btn btn-sm radius-8 px-20 py-10 btn-ledger-clear"
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="card border shadow-sm radius-16 ledger-table-card overflow-hidden">
        <div className="card-body p-24 pb-0">
          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 mb-2">
            <div style={{ minWidth: "92px" }}>
              <SearchableSelect
                placeholder="Show"
                value={String(limitVal)}
                showSearch={false}
                onChange={handleLimitChange}
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  label: String(n),
                  value: String(n),
                }))}
              />
            </div>
            <div
              className="position-relative flex-grow-1"
              style={{ maxWidth: "280px" }}
            >
              <Icon
                icon="ion:search-outline"
                className="icon position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light"
              />
              <input
                type="search"
                className="form-control ps-40 h-40-px radius-12"
                placeholder="Search by Ac No., Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive px-16 pb-16">
          {error ? (
            <div className="text-center py-5 text-danger fw-medium">
              {error}
            </div>
          ) : displayRows.length === 0 && !loading ? (
            <div className="text-center py-5 text-secondary-light fw-medium">
              No data available in table
            </div>
          ) : (
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>SR</th>
                  <th>AC No.</th>
                  <th>AC Type</th>
                  <th>Name</th>
                  <th>Balance (Rs.)</th>
                  <th className="text-center">Ledger</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row) => (
                  <tr key={row._id}>
                    <td className="text-secondary-light">{row.sr}</td>
                    <td className="text-secondary-light">{row.acNo}</td>
                    <td className="text-secondary-light">
                      <span className={`badge radius-4 px-8 py-4 text-xs ${getRoleBadgeClass(row.role)}`}>
                        {row.role?.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-secondary-light">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span>{row.name}</span>
                        {row.isMaster && (
                          <span className="badge bg-danger-focus text-danger-600 radius-4 px-8 py-4 text-xs">
                            Master
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`fw-bold ${row.balanceClass}`}>
                      {row.balance}
                    </td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="ledger-action-btn"
                        onClick={() => handleLedgerClick(row)}
                        aria-label="View ledger"
                      >
                        <Icon icon="tabler:file-text" className="icon" width={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-top d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-24">
          <p className="mb-0 text-secondary-light text-sm">
            Showing{" "}
            <span className="fw-medium text-primary-light">
              {ledgerRows.length > 0
                ? `${(currentPage - 1) * limitVal + 1} to ${Math.min(
                  currentPage * limitVal,
                  total,
                )} of ${total} entries`
                : "0 to 0 of 0 entries"}
            </span>
          </p>
          {totalPages > 0 && (
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrevPage}
              >
                <Icon icon="tabler:chevron-left" className="icon" />
              </button>
              {pageButtons.map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  className={`btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center ${pageNum === currentPage
                    ? "ledger-pagination-btn active-page-num"
                    : "bg-base text-secondary-light"
                    }`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              ))}
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span className="text-secondary-light px-1">…</span>
                  <button
                    type="button"
                    className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center bg-base text-secondary-light"
                    onClick={() => handlePageChange(totalPages)}
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage}
              >
                <Icon icon="tabler:chevron-right" className="icon" />
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedAccount && (
        <LedgerModal
          show={!!selectedAccount && isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedAccount(null);
          }}
          accountName={selectedAccount?.name ?? ""}
          accountId={selectedAccount?.id ?? ""}
          accountDbId={selectedAccount?.dbId}
        />
      )}
    </>
  );
};

export default LedgerLayer;
